<template>
  <SoSkeleton
    v-if="isLoading"
    type="list-item-three-line"
    height="30"
    width="30"
  />
  <div v-else-if="services.length > 0" class="ml-4">
    <SoForm ref="soForm" v-model="isValid">
      <div v-for="service in flatServices" :key="service.id" class="mb-4">
        <div class="d-flex align-center ga-2">
          <SoCheckbox
            v-model="serviceIdsToStore"
            :disabled="isServiceDisabled(service)"
            :value="service.id"
            :label="service.label"
            multiple
            @update:model-value="handleSpecificChange(service.id, $event)"
          />
          <SoTooltip
            v-if="isServiceLockedForMissingPrimaryEmail(service)"
            text="The customer has no primary contact email, so accessible paper statements cannot be removed — they would have no way to receive bills. Please add an email to the primary contact."
            data-testid="psr-paper-statement-lock-tooltip"
          >
            <SoIcon icon="help-circle" size="small" />
          </SoTooltip>
        </div>
        <div
          v-for="[serviceItemId, serviceItem] in getSortedServiceItems(
            service.id,
          )"
          v-show="serviceIdsToStore.includes(service.id)"
          :key="serviceItemId"
          class="mt-4 my-6"
        >
          <SoTextField
            v-if="isText(serviceItem.type)"
            v-model="serviceItem.value"
            class="w-75"
            :label="serviceItem.description"
            :placeholder="serviceItem.placeholder"
            :rules="validationRules(serviceItem.description)"
          />
          <SoSelect
            v-else-if="isSelect(serviceItem.type)"
            v-model="serviceItem.valueSelectItem"
            class="w-75"
            :label="serviceItem.description"
            :placeholder="serviceItem.placeholder"
            :items="getSelectItems(serviceItem, service.serviceItems)"
            :rules="validationRules(serviceItem.description)"
          />
          <SoDatePicker
            v-else-if="isDate(serviceItem.type)"
            v-model="serviceItem.valueDate"
            class="w-75 mt-8"
            :label="serviceItem.description"
            :error="error"
            @update:model-value="setError(serviceItem.valueDate)"
          />
        </div>
      </div>
      <PsrSoSupportiveServices
        v-if="isNewPsrServicesEnabled && groupedNewServices.length > 0"
        :categories="groupedNewServices"
      >
        <template #default="{ service }">
          <SoCheckbox
            v-model="serviceIdsToStore"
            :disabled="isServiceDisabled(service)"
            :value="service.id"
            :label="service.label"
            multiple
            @update:model-value="handleSpecificChange(service.id, $event)"
          />
          <div
            v-for="[serviceItemId, serviceItem] in getSortedServiceItems(
              service.id,
            )"
            v-show="serviceIdsToStore.includes(service.id)"
            :key="serviceItemId"
            class="mt-4 my-6"
          >
            <SoTextField
              v-if="isText(serviceItem.type)"
              v-model="serviceItem.value"
              class="w-75"
              :label="serviceItem.description"
              :placeholder="serviceItem.placeholder"
              :rules="validationRules(serviceItem.description)"
            />
            <SoSelect
              v-else-if="isSelect(serviceItem.type)"
              v-model="serviceItem.valueSelectItem"
              class="w-75"
              :label="serviceItem.description"
              :placeholder="serviceItem.placeholder"
              :items="getSelectItems(serviceItem, service.serviceItems)"
              :rules="validationRules(serviceItem.description)"
            />
            <SoDatePicker
              v-else-if="isDate(serviceItem.type)"
              v-model="serviceItem.valueDate"
              class="w-75 mt-8"
              :label="serviceItem.description"
              :error="error"
              @update:model-value="setError(serviceItem.valueDate)"
            />
          </div>
        </template>
      </PsrSoSupportiveServices>
    </SoForm>
  </div>

  <SoStatus
    v-else
    text="The customer is not eligible for any priority services"
    class="ml-2"
    data-testid="no-services"
  />
  <SoAlert
    v-if="showAlert"
    text="Unable to save PSR. Please select a vulnerability and service to proceed."
    type="error"
    class="ml-2 mt-5"
  />
  <div class="d-flex align-center justify-end mt-8 ga-2">
    <SoButton title="cancel" type="secondary" large @click="$emit('cancel')" />
    <SoButton
      title="confirm"
      :disabled="isLoading || submitMutator.isPending"
      :loading="submitMutator.isPending"
      type="success"
      large
      submit
      @click="saveVulnerabilitiesServices()"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    IPsrExtraData,
    IPsrPriorityService,
    IPsrPriorityServiceItem,
    PsrPriorityServiceItemFieldType,
    PsrPriorityServiceItemFieldTypes,
    PsrPriorityServiceItemType,
    IPsrPriorityServiceItemTypes,
  } from "@/models/psr/psr.types"
  import { PaperBillStateResponsePaperBillType } from "@/api/backofficeRest/generated/model"
  import dayjs from "dayjs"
  import SoStatus from "@/components/SoStatus.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { SoSelectItem, SoSelectValue } from "@/types/components/SoSelect"
  import SoButton from "@/components/formControls/SoButton.vue"
  import {
    IStorePsr,
    IStorePsrCustomerService,
  } from "@/mutators/psr/PsrMutator.types"
  import { PsrService } from "@/services/PsrService"
  import {
    formatDateDashed,
    formatDateSlashed,
    formatServerDateTimeNow,
  } from "@/helpers/dateHelper"
  import { ADULT_AGE } from "@/validators/dateOfBirth"
  import rules from "@/validators/rules"
  import { ValidationRule } from "@/types/ValidationRule"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import { errorReporter } from "@/services/ErrorService"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import PsrSoSupportiveServices, {
    PsrSoSupportiveServiceCategory,
  } from "@/views/customer/overview/modals/PsrSoSupportiveServices.vue"

  interface PsrCustomerServiceItem {
    id: number
    displayOrder: number
    description: PsrPriorityServiceItemFieldType
    label: string
    required: boolean
    type: PsrPriorityServiceItemType
    field: string
    placeholder?: string
    value?: string
    valueDate?: Date
    valueSelectItem?: SoSelectValue
  }
  type ServiceItemsToStore = Map<number, PsrCustomerServiceItem>

  interface Service {
    startDate: string
    serviceItems: ServiceItemsToStore
  }

  export default defineComponent({
    components: {
      SoForm,
      SoButton,
      SoSelect,
      SoDatePicker,
      SoTextField,
      SoCheckbox,
      SoStatus,
      SoSkeleton,
      SoAlert,
      SoTooltip,
      SoIcon,
      PsrSoSupportiveServices,
    },

    emits: ["cancel", "submit"],
    data() {
      const current = useCurrentAccountStore()
      return {
        current,
        customerServices: [] as (boolean | null)[],
        isValid: true,
        serviceIdsToStore: [] as number[],
        initiallySelectedServiceIds: [] as number[],
        psrService: PsrService,
        servicesToStore: new Map() as Map<number, Service>,
        error: "",
        showAlert: false,
        submitMutator: PsrService.storeVulnerabilitiesAndAssociatedServices(),
      }
    },
    computed: {
      services(): Array<IPsrPriorityService> {
        return this.psrService.getServicesByVulnerabilityIds(
          this.vulnerabilityIds,
        ).data
      },
      isLoading() {
        return this.psrService.getServicesByVulnerabilityIds(
          this.vulnerabilityIds,
        ).isFetching
      },
      vulnerabilityIds() {
        return this.current.vulnerabilities
          .map((vulnerability) => vulnerability.id)
          .join(",")
      },
      primaryContactEmail(): string {
        const primary = this.current.accountContacts.data?.find(
          (contact) => contact.juniferPrimaryContact,
        )
        return primary?.email ?? ""
      },
      paperStatementServiceId(): number | null {
        return (
          this.services.find((service) =>
            service.serviceItems?.some(
              (item) =>
                item.description ===
                PsrPriorityServiceItemFieldTypes.PAPER_STATEMENT_TYPE,
            ),
          )?.id ?? null
        )
      },
      isPaperStatementLockedForMissingPrimaryEmail(): boolean {
        if (this.current.accountContacts.isFetching) return false
        const id = this.paperStatementServiceId
        return (
          id != null &&
          this.initiallySelectedServiceIds.includes(id) &&
          !this.primaryContactEmail
        )
      },
      hasStandardPaperBillSecondary(): boolean {
        return (
          this.current.paperBillState.data.paperBillType ===
          PaperBillStateResponsePaperBillType.STANDARD
        )
      },
      isNewPsrServicesEnabled(): boolean {
        return (
          FeaturesService.isFeatureActive(
            "TMP_SO_32650_SO_SUPPORTIVE_SERVICES",
          ) ?? false
        )
      },
      flatServices(): Array<IPsrPriorityService> {
        return this.services.filter((service) => !service.isInternal)
      },
      groupedNewServices(): Array<PsrSoSupportiveServiceCategory> {
        const newServices = this.services.filter(
          (service) => service.isInternal,
        )
        const categoriesByName = new Map<
          string,
          PsrSoSupportiveServiceCategory
        >()

        newServices
          .slice()
          .sort(
            (a, b) =>
              (b.category?.displayOrder ?? 0) - (a.category?.displayOrder ?? 0),
          )
          .forEach((service) => {
            const title = service.category?.name ?? "Other"
            const existing = categoriesByName.get(title)
            if (existing) {
              existing.services.push(service)
            } else {
              categoriesByName.set(title, { title, services: [service] })
            }
          })

        return [...categoriesByName.values()]
      },
    },
    watch: {
      services: {
        deep: true,
        immediate: true,
        handler() {
          this.initialiseSelectedServices()
        },
      },
      "current.paperBillState.data.paperBillType"() {
        this.initialiseSelectedServices()
      },
    },

    methods: {
      initialiseSelectedServices() {
        const availablePriorityServices = this.services
        this.serviceIdsToStore = []
        this.initiallySelectedServiceIds = []
        if (!availablePriorityServices.length) return

        this.servicesToStore = new Map()

        const customerServices =
          this.current.customerSettings.data.customerServices ?? []

        availablePriorityServices.forEach((availableService) => {
          const customerServiceEnabled = customerServices.find(
            (svc) => svc.id === availableService.id,
          )

          if (customerServiceEnabled) {
            this.initiallySelectedServiceIds.push(availableService.id)
          }

          if (customerServiceEnabled || availableService.alwaysOn) {
            this.serviceIdsToStore.push(availableService.id)

            this.applyContentToForm(
              availableService,
              customerServiceEnabled?.extraData,
            )
          }
        })

        const paperStatementId = this.paperStatementServiceId
        if (
          this.hasStandardPaperBillSecondary &&
          paperStatementId != null &&
          !this.serviceIdsToStore.includes(paperStatementId)
        ) {
          const paperStatementService = availablePriorityServices.find(
            (service) => service.id === paperStatementId,
          )
          if (paperStatementService) {
            this.serviceIdsToStore.push(paperStatementId)
            this.applyContentToForm(paperStatementService)
          }
        }
      },
      isServiceDisabled(service: IPsrPriorityService): boolean {
        return (
          service.alwaysOn ||
          this.isServiceLockedForMissingPrimaryEmail(service)
        )
      },
      isServiceLockedForMissingPrimaryEmail(
        service: IPsrPriorityService,
      ): boolean {
        return (
          service.id === this.paperStatementServiceId &&
          this.isPaperStatementLockedForMissingPrimaryEmail
        )
      },
      setError(value?: Date): void {
        const date = dayjs(value)
        const dateIsNotSet = value === undefined
        const dateIsNotValid =
          !date.isValid() ||
          !date.isBefore(dayjs().subtract(ADULT_AGE, "years"))

        if (dateIsNotSet) this.error = "Field is required"
        else if (dateIsNotValid)
          this.error = "Please enter a valid date of birth"
        else this.error = ""
      },
      validationRules(type: PsrPriorityServiceItemFieldType): ValidationRule[] {
        const fieldRules: ValidationRule[] = [rules.required]
        switch (type) {
          case PsrPriorityServiceItemFieldTypes.FIRST_NAME:
            fieldRules.push(rules.nameRule)
            break
          case PsrPriorityServiceItemFieldTypes.LAST_NAME:
            fieldRules.push(rules.nameRule)
            break
          case PsrPriorityServiceItemFieldTypes.EMAIL:
            fieldRules.push(rules.email)
            break
          case PsrPriorityServiceItemFieldTypes.PHONE:
            fieldRules.push(rules.phoneNumber)
            break
          case PsrPriorityServiceItemFieldTypes.DOB:
            fieldRules.push(rules.dateOfBirth)
            break
          default:
            break
        }
        return fieldRules
      },
      isText(serviceItemType: string) {
        return serviceItemType === IPsrPriorityServiceItemTypes.TEXT
      },
      isSelect(serviceItemType: string) {
        return serviceItemType === IPsrPriorityServiceItemTypes.SELECT
      },
      isDate(serviceItemType: string) {
        return serviceItemType === IPsrPriorityServiceItemTypes.DATE
      },
      getSortedServiceItems(
        serviceId: number,
      ): ServiceItemsToStore | undefined {
        const items = this.servicesToStore.get(serviceId)?.serviceItems
        if (!items) return

        return new Map(
          [...items.entries()].sort(
            (a, b) => a[1].displayOrder - b[1].displayOrder,
          ),
        )
      },
      getSelectItems(
        service: PsrCustomerServiceItem,
        serviceItems?: IPsrPriorityServiceItem[],
      ): Array<SoSelectItem> | undefined {
        return serviceItems
          ?.filter(
            (item) =>
              item.name === service.field &&
              item.type === IPsrPriorityServiceItemTypes.SELECT_ITEM,
          )
          .map((item) => item.description)
      },
      async saveVulnerabilitiesServices() {
        const soForm = this.$refs.soForm as InstanceType<typeof SoForm>
        const { valid } = await soForm.validate()
        if (!valid) return

        if (!this.serviceIdsToStore.length) {
          this.showAlert = true
          setTimeout(() => (this.showAlert = false), 3000)
          return
        }
        const customerServices: IStorePsrCustomerService[] = []
        this.servicesToStore.forEach((service: Service, id: number) => {
          if (!service.serviceItems) return

          const extraData = this.applyExtraDataForService(service.serviceItems)
          customerServices.push({
            extraData: extraData.length > 0 ? extraData : undefined,
            id,
            startDate: formatDateDashed(dayjs(service.startDate)),
          })
        })
        if (this.error) return

        const customerId = this.current.customerId.data
        if (!customerId) {
          throw new Error("CustomerId not found")
        }
        const accountId = this.current.coreId.data
        if (!accountId) {
          throw new Error("AccountId not found")
        }

        const payload: IStorePsr = {
          customerId,
          input: {
            accountId,
            customerServices,
            customerVulnerabilities: this.current.vulnerabilities,
          },
        }
        try {
          await this.submitMutator.mutateAsync(payload)
          this.current.customerSettings.refetch()
          this.current.paperBillState.refetch()
          this.current.accountContacts.refetch()
        } catch (err) {
          errorReporter.report(err)
        }
        this.$emit("submit")
      },
      applyExtraDataForService(
        serviceItems: ServiceItemsToStore,
      ): Array<IPsrExtraData> {
        const extraData: Array<IPsrExtraData> = []

        serviceItems.forEach((item: PsrCustomerServiceItem, id: number) => {
          if (item.type === IPsrPriorityServiceItemTypes.SELECT_ITEM) return
          let value: string | undefined
          const selectValue: SoSelectValue | undefined = item.valueSelectItem

          switch (item.type) {
            case IPsrPriorityServiceItemTypes.DATE:
              this.setError(item.valueDate)
              if (this.error) return
              value = formatDateSlashed(dayjs(item.valueDate))
              break

            case IPsrPriorityServiceItemTypes.SELECT:
              value =
                selectValue && typeof selectValue !== "object"
                  ? String(selectValue)
                  : undefined
              break

            default:
              value = item.value
          }

          if (!value) {
            throw new Error("Field values should be set by this point")
          }
          extraData.push({
            field: item.field,
            id,
            value,
          })
        })
        return extraData
      },
      handleSpecificChange(serviceId: number, checked: number[]) {
        if (
          checked.includes(serviceId) &&
          !this.servicesToStore.has(serviceId)
        ) {
          const serviceItems: ServiceItemsToStore = new Map()
          this.services
            .find((service) => service.id === serviceId)
            ?.serviceItems?.forEach((serviceItem) => {
              serviceItems.set(serviceItem.id, {
                id: serviceItem.id,
                field: serviceItem.name,
                type: serviceItem.type,
                displayOrder: serviceItem.displayOrder,
                description: serviceItem.description,
                placeholder: serviceItem.placeholder,
                required: serviceItem.required,
                label: serviceItem.label,
              })
            })
          this.servicesToStore.set(serviceId, {
            startDate: formatServerDateTimeNow(),
            serviceItems,
          })
        } else {
          this.servicesToStore.delete(serviceId)
        }
      },
      applyContentToForm(
        service: IPsrPriorityService,
        extraData?: IPsrExtraData[],
      ) {
        const serviceItems: ServiceItemsToStore = new Map()
        const extraDataToPopulate: Array<IPsrExtraData> = extraData ?? []

        for (const extraDataItem of extraDataToPopulate) {
          const serviceItem: IPsrPriorityServiceItem | undefined =
            service?.serviceItems?.find(
              (serviceItem) => serviceItem.id === extraDataItem.id,
            )
          if (serviceItem == undefined) {
            throw new Error("Service item should be set by this point")
          }

          const valueDate =
            serviceItem.type === IPsrPriorityServiceItemTypes.DATE
              ? dayjs(extraDataItem.value, "DD/MM/YYYY").toDate()
              : undefined

          const valueSelectItem =
            serviceItem.type === IPsrPriorityServiceItemTypes.SELECT
              ? extraDataItem.value
              : undefined

          serviceItems.set(extraDataItem.id, {
            id: extraDataItem.id,
            field: extraDataItem.field,
            value: extraDataItem.value,
            valueDate,
            valueSelectItem,
            type: serviceItem.type,
            displayOrder: serviceItem.displayOrder,
            description: serviceItem.description,
            placeholder: serviceItem.placeholder,
            required: serviceItem.required,
            label: serviceItem.label,
          })
        }

        // this is map for any extra data we can have under checkboxes
        this.servicesToStore.set(service.id, {
          startDate: formatDateDashed(dayjs()),
          serviceItems,
        })
      },
    },
  })
</script>
